// MaterialDetailModal.jsx
import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Divider,
  Avatar,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import PublicIcon from "@mui/icons-material/Public";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import HandymanIcon from "@mui/icons-material/Handyman";

const BLUE = "#052660";

// ---- Image carousel dialog ----
const ImageCarouselDialog = ({ open, images, startIndex, onClose }) => {
  const [index, setIndex] = useState(startIndex || 0);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  React.useEffect(() => {
    if (open) setIndex(startIndex || 0);
  }, [open, startIndex]);

  if (!images || images.length === 0) return null;

  const prev = () => setIndex((i) => (i - 1 + images.length) % images.length);
  const next = () => setIndex((i) => (i + 1) % images.length);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", top: 10, right: 10, zIndex: 2, bgcolor: "#fff", "&:hover": { bgcolor: "#f1f4f9" } }}
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <DialogContent
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: { xs: "70vh", sm: "28em" },
          position: "relative",
          bgcolor: "#0d0d0d",
          padding: { xs: "1em", sm: "1.5em" },
        }}
      >
        {images.length > 1 && (
          <IconButton
            onClick={prev}
            sx={{ position: "absolute", left: { xs: 4, sm: 12 }, color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.22)" } }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        )}
        <img
          src={images[index]}
          alt={`material-${index}`}
          style={{ maxWidth: "100%", maxHeight: "65vh", objectFit: "contain", borderRadius: "0.5em" }}
        />
        {images.length > 1 && (
          <IconButton
            onClick={next}
            sx={{ position: "absolute", right: { xs: 4, sm: 12 }, color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.22)" } }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        )}
        {images.length > 1 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              color: "#fff",
              fontSize: ".8em",
              fontWeight: 600,
              bgcolor: "rgba(255,255,255,0.15)",
              px: 1.5,
              py: 0.4,
              borderRadius: "1em",
              letterSpacing: ".02em",
            }}
          >
            {index + 1} / {images.length}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---- Section label with icon ----
const SectionLabel = ({ icon, children }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ marginBottom: "0.6em" }}>
    <Avatar sx={{ width: 30, height: 30, bgcolor: `${BLUE}14`, color: BLUE }}>
      {icon}
    </Avatar>
    <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#1e293b" }}>
      {children}
    </Typography>
  </Stack>
);

// ---- MPN chip list (non-clickable) ----
const MpnChipList = ({ label, mpns }) => {
  const hasValues = mpns && mpns.length > 0;
  return (
    <Box sx={{ marginTop: "0.9em" }}>
      <Typography
        variant="caption"
        sx={{
          color: "#334155",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: ".04em",
          fontSize: ".7em",
        }}
      >
        {label}
      </Typography>
      {hasValues ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ marginTop: "0.4em" }}>
          {mpns.map((m, idx) => {
            const isMissing = !m.mpn_name;
            return (
              <Chip
                key={idx}
                size="small"
                label={
                  isMissing
                    ? "Not Present"
                    : `${(m.mpn_name || "Not Present").toString().trim()}  •  ${(m.mpn_number || "Not Present").toString().trim()}`
                }
                sx={
                  isMissing
                    ? {
                        bgcolor: "transparent",
                        color: "#a0aec0",
                        fontWeight: 600,
                        fontSize: ".78em",
                        fontStyle: "italic",
                        border: "none",
                        maxWidth: "100%",
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          paddingLeft: 0,
                        },
                      }
                    : {
                        bgcolor: "#f1f4f9",
                        color: "#334155",
                        fontWeight: 600,
                        fontSize: ".78em",
                        borderRadius: "0.5em",
                        border: "1px solid #e2e8f0",
                        maxWidth: "100%",
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        },
                      }
                }
              />
            );
          })}
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ color: "#a0aec0", fontWeight: 600, marginTop: "0.4em", fontStyle: "italic" }}>
          Not Present
        </Typography>
      )}
    </Box>
  );
};

// ---- Info table row (label / value pairs) ----
// Always renders every row, showing "Not Present" (in a faded color) for any missing/undefined/null value,
// and keeps a fixed non-wrapping label column so every value starts at the same x-position.
const InfoTable = ({ rows }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <Box sx={{ borderRadius: "0.75em", overflow: "hidden", border: "1px solid #e6eaf0" }}>
      <Table size="small">
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow
              key={idx}
              sx={{
                "&:last-child td": { borderBottom: 0 },
                backgroundColor: idx % 2 === 0 ? "#fff" : "#f8fafc",
              }}
            >
              <TableCell
                sx={{
                  width: { xs: "10em", sm: "14em" },
                  fontWeight: 600,
                  color: "#64748b",
                  fontSize: { xs: ".72em", sm: ".78em" },
                  textTransform: "uppercase",
                  letterSpacing: ".03em",
                  whiteSpace: "nowrap",
                  borderColor: "#eef1f6",
                }}
              >
                {row.label}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: row.value ? "#1e293b" : "#a0aec0",
                  fontStyle: row.value ? "normal" : "italic",
                  borderColor: "#eef1f6",
                  wordBreak: "break-word",
                }}
              >
                {row.value || "Not Present"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

// ---- Main modal ----
const MaterialDetailModal = ({ open, onClose, materialData = [], locationData = [] }) => {
  const [carouselOpen, setCarouselOpen] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const groupedLocations = useMemo(() => {
    const map = {};
    locationData.forEach((row) => {
      const key = row.storage_loc || "__NOT_FOUND__";
      if (!map[key]) {
        map[key] = {
          storage_loc: row.storage_loc,
          abc_class: row.abc_class,
          material_criticality_indicator: row.material_criticality_indicator,
          bins: [],
        };
      }
      map[key].bins.push({
        bin_location: row.bin_location,
        ohq: row.ohq,
      });
    });
    return Object.values(map);
  }, [locationData]);

  const getGlobalMpns = (mat) => {
    if (mat.mpn_links && mat.mpn_links.length > 0) return mat.mpn_links;
    const flat = [];
    for (let i = 1; i <= 5; i++) {
      const name = mat[`mpn_name${i}`];
      const number = mat[`mpn_number${i}`];
      if (name && number) flat.push({ mpn_name: name, mpn_number: number });
    }
    return flat;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: "1.25em" },
            height: { xs: "100%", sm: "85vh" },
            maxHeight: { xs: "100%", sm: "85vh" },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "1em 1.1em", sm: "1.25em 1.75em" },
            paddingBottom: { xs: "0.75em", sm: "0.9em" },
            borderBottom: "1px solid #eef1f6",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar sx={{ bgcolor: `${BLUE}1a`, color: BLUE, width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 }, flexShrink: 0 }}>
              <Inventory2Icon />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2} sx={{ fontSize: { xs: "1.05em", sm: "1.25em" } }}>
                Material Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: ".78em", sm: ".875em" } }}>
                Full material and storage information
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            sx={{ bgcolor: "#f1f4f9", "&:hover": { bgcolor: "#e2e8f0" }, flexShrink: 0, ml: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: { xs: "0.85em", sm: "1.1em" },
            //paddingTop: { xs: "0.75em", sm: "0.9em" },
            marginTop:{ xs: "0.75em", sm: "0.9em" },
            //bgcolor: "#f8fafc",
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Stack spacing={{ xs: "0.75em", sm: "0.9em" }}>
          {materialData.map((mat, i) => {
            const images = mat.image_link || [];
            const globalMpns = getGlobalMpns(mat);
            const localMpns = mat.local_mpn || [];

            const globalMaterialValue =
              mat.gbl_material_number || mat.gbl_material_desc
                ? `${mat.gbl_material_number || "Not Present"}${
                    mat.gbl_material_desc ? "  —  " + mat.gbl_material_desc : ""
                  }`
                : null;

            return (
              <Paper
                key={mat.id || i}
                elevation={0}
                sx={{
                  borderRadius: "1em",
                  border: "1px solid #e6eaf0",
                  padding: { xs: "0.9em", sm: "1.1em" },
                  boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
                }}
              >
                {/* ---- Material / UOM / View Images header ----
                    Grid: [material info] [UOM - centered] [view images]
                    Stacks vertically on mobile, UOM stays centered within its own row */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr" },
                    alignItems: "center",
                    columnGap: 2,
                    rowGap: 1.5,
                    marginBottom: "0.85em",
                  }}
                >
                  {/* Left: material no + description */}
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ minWidth: 0, order: { xs: 1, sm: 1 } }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: `${BLUE}14`,
                        color: BLUE,
                        width: { xs: 40, sm: 46 },
                        height: { xs: 40, sm: 46 },
                        flexShrink: 0,
                      }}
                    >
                      <HandymanIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{ color: "#0f172a", wordBreak: "break-word", fontSize: { xs: "1em", sm: "1.25em" } }}
                      >
                        {mat.material_no || "Not Present"}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, wordBreak: "break-word" }}
                      >
                        {mat.material_desc || "Not Present"}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Center: UOM */}
                  {mat.b_un && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        order: { xs: 2, sm: 2 },
                      }}
                    >
                      <Chip
                        size="small"
                        label={`UOM : ${mat.b_un}`}
                        sx={{
                          bgcolor: "#eef2fb",
                          color: BLUE,
                          fontWeight: 700,
                          fontSize: "0.9em",
                          height: "auto",
                          px: 1,
                          py: 1.6,
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    </Box>
                  )}

                  {/* Right: view images */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "flex-start", sm: "flex-end" },
                      order: { xs: 3, sm: 3 },
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.8}
                      alignItems="center"
                      onClick={() => {
                        if (images.length > 0) setCarouselOpen(true);
                      }}
                      sx={{
                        cursor: images.length > 0 ? "pointer" : "not-allowed",
                        bgcolor: images.length > 0 ? BLUE : "#cbd5e1",
                        color: "#fff",
                        borderRadius: "0.7em",
                        px: 2,
                        py: 1,
                        fontWeight: 700,
                        boxShadow: images.length > 0 ? `0 6px 16px ${BLUE}40` : "none",
                        transition: "all .18s ease",
                        width: { xs: "100%", sm: "auto" },
                        justifyContent: "center",
                        opacity: images.length > 0 ? 1 : 0.65,
                        pointerEvents: images.length > 0 ? "auto" : "none",
                        "&:hover": images.length > 0
                          ? {
                              bgcolor: `${BLUE}dd`,
                              transform: "translateY(-2px)",
                            }
                          : {},
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: "#fff", whiteSpace: "nowrap" }}
                      >
                        View {images.length > 1 ? `Images (${images.length})` : "Image"}
                      </Typography>
                    </Stack>
                  </Box>
                </Box>

                <Divider sx={{ marginBottom: "0.85em" }} />

                <SectionLabel icon={<Inventory2Icon fontSize="small" />}>
                  Item Information
                </SectionLabel>
                <InfoTable
                  rows={[
                    { label: "Manufacturer", value: mat.manufacturer_name },
                    { label: "Manufacturer Item Code", value: mat.manufac_item_code },
                    { label: "Global Material", value: globalMaterialValue },
                  ]}
                />

                <Box sx={{ marginTop: "1em" }}>
                  <SectionLabel icon={<PublicIcon fontSize="small" />}>
                    Part Numbers
                  </SectionLabel>
                  <MpnChipList label="Global MPN" mpns={globalMpns} />
                  <MpnChipList label="Local MPN" mpns={localMpns} />
                </Box>
              </Paper>
            );
          })}

          <Paper
            elevation={0}
            sx={{
              borderRadius: "1em",
              border: "1px solid #e6eaf0",
              padding: { xs: "0.9em", sm: "1.1em" },
              boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
            }}
          >
            <SectionLabel icon={<WarehouseIcon fontSize="small" />}>
              Storage Locations
            </SectionLabel>

            <Box
              sx={{
                borderRadius: "0.9em",
                overflow: "hidden",
                border: "1px solid #e2e8f0",
              }}
            >
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 560, "& td, & th": { padding: "0.4em 0.75em" } }}>
                  <TableHead>
                    <TableRow sx={{ "& th": { backgroundColor: "#f1f4f9", fontWeight: 700, fontSize: ".78em", color: "#475569", textTransform: "uppercase", letterSpacing: ".03em", whiteSpace: "nowrap" } }}>
                      <TableCell>Storage Location</TableCell>
                      <TableCell>Bin Location</TableCell>
                      <TableCell>OHQ</TableCell>
                      <TableCell>ABC Class</TableCell>
                      <TableCell>Criticality</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedLocations.map((loc) =>
                      loc.bins.map((bin, idx) => (
                        <TableRow
                          key={`${loc.storage_loc}-${idx}`}
                          sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                        >
                          {idx === 0 && (
                            <TableCell
                              rowSpan={loc.bins.length}
                              sx={{
                                fontWeight: loc.storage_loc ? 700 : 600,
                                verticalAlign: "top",
                                color: loc.storage_loc ? BLUE : "#a0aec0",
                                fontStyle: loc.storage_loc ? "normal" : "italic",
                                borderRight: "1px solid #f1f4f9",
                              }}
                            >
                              {loc.storage_loc || "Not Present"}
                            </TableCell>
                          )}
                          <TableCell
                            sx={{
                              fontWeight: bin.bin_location ? 500 : 600,
                              color: bin.bin_location ? "inherit" : "#a0aec0",
                              fontStyle: bin.bin_location ? "normal" : "italic",
                            }}
                          >
                            {bin.bin_location || "Not Present"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: (bin.ohq ?? "") === "" && bin.ohq !== 0 ? "#a0aec0" : "inherit",
                              fontStyle: (bin.ohq ?? "") === "" && bin.ohq !== 0 ? "italic" : "normal",
                            }}
                          >
                            {bin.ohq ?? "Not Present"}
                          </TableCell>
                          {idx === 0 && (
                            <TableCell rowSpan={loc.bins.length} sx={{ verticalAlign: "top" }}>
                              {loc.abc_class ? (
                                <Chip size="small" label={loc.abc_class} sx={{ fontWeight: 700, bgcolor: "#eef2fb", color: BLUE }} />
                              ) : (
                                <Box component="span" sx={{ color: "#a0aec0", fontWeight: 600, fontStyle: "italic" }}>
                                  Not Present
                                </Box>
                              )}
                            </TableCell>
                          )}
                          {idx === 0 && (
                            <TableCell rowSpan={loc.bins.length} sx={{ verticalAlign: "top" }}>
                              {loc.material_criticality_indicator ? (
                                loc.material_criticality_indicator
                              ) : (
                                <Box component="span" sx={{ color: "#a0aec0", fontWeight: 600, fontStyle: "italic" }}>
                                  Not Present
                                </Box>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                    {groupedLocations.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ padding: "2em", color: "#94a3b8" }}>
                          No location data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Paper>
          </Stack>
        </DialogContent>
      </Dialog>

      <ImageCarouselDialog
        open={carouselOpen}
        images={materialData.find((m) => m.image_link?.length > 0)?.image_link || []}
        startIndex={0}
        onClose={() => setCarouselOpen(false)}
      />
    </>
  );
};

export default MaterialDetailModal;